import {
  CycleCountConfig,
  CycleCountStatus,
} from '../models/cycle-count-config.model';

/**
 * True when the admin row can resume/continue the warehouse's current session.
 * Draft and InProgress are both live sessions that navigate to the count screen;
 * a session id is required because navigation is by id. Complete/Adjusted sessions
 * are done and are handled by {@link canViewVarianceReport} instead.
 *
 * BUG-30361: InProgress was previously excluded, dead-ending the row with no resume path.
 */
export function canResumeCycleCount(
  config: Pick<
    CycleCountConfig,
    'cycleCountId' | 'cycleCountStatus' | 'lastRunDate'
  >
): boolean {
  return (
    !!config.cycleCountId &&
    !!config.lastRunDate &&
    (config.cycleCountStatus === 'Draft' ||
      config.cycleCountStatus === 'InProgress')
  );
}

/**
 * True when the warehouse's CURRENT session is itself closed (Complete or Adjusted) and so has its
 * own variance report. Keep this meaning: the mobile admin page branches on it to choose between the
 * session's own report and the warehouse-history view. For "can the web View Details open anything",
 * use {@link varianceReportTargetId} instead (#35726).
 *
 * BUG-30361: Adjusted was previously excluded, hiding the report from adjusted counts.
 */
export function canViewVarianceReport(
  config: Pick<CycleCountConfig, 'cycleCountId' | 'cycleCountStatus'>
): boolean {
  return (
    !!config.cycleCountId &&
    (config.cycleCountStatus === 'Complete' ||
      config.cycleCountStatus === 'Adjusted')
  );
}

/**
 * #35726: the count whose variance report the web View Details opens: the current session when it is
 * closed ({@link canViewVarianceReport}), otherwise the warehouse's most recent completed count,
 * otherwise null. Once Generate makes a new Draft the current session, this keeps the completed
 * count's Variance Review (and Approve & Post) reachable.
 */
export function varianceReportTargetId(
  config: Pick<
    CycleCountConfig,
    'cycleCountId' | 'cycleCountStatus' | 'lastCompletedCycleCountId'
  >
): string | null {
  if (canViewVarianceReport(config)) return config.cycleCountId;
  return config.lastCompletedCycleCountId || null;
}

/**
 * True when the admin row's count can be REOPENED (#30580). Only a closed count (Complete or Adjusted)
 * with a session id can be reopened — reopening transitions it back to InProgress so more items can be
 * added and counted. Mirrors the server-side {@link CycleCountUtils.CanReopen} rule.
 */
export function canReopenCount(
  config: Pick<CycleCountConfig, 'cycleCountId' | 'cycleCountStatus'>
): boolean {
  return (
    !!config.cycleCountId &&
    (config.cycleCountStatus === 'Complete' ||
      config.cycleCountStatus === 'Adjusted')
  );
}

/**
 * True when the admin row can GENERATE a new count for the warehouse — i.e. it has no LIVE,
 * RESUMABLE session. A live session is a Draft/InProgress count that still has a `lastRunDate`
 * (the exact condition {@link canResumeCycleCount} gates Start/Resume on); for those the admin
 * uses Start/Resume, not Generate. Every other state can start a fresh count on demand:
 *  - `null` — never counted (the required first manual run; the scheduler skips never-run warehouses),
 *  - `Complete` / `Adjusted` — a finished count being re-run manually (before its scheduled date),
 *  - a Draft/InProgress whose `lastRunDate` was cleared by a Frequency/Count-Size edit (#30359 nulls
 *    LastRunDate on the latest row but leaves its Status intact). That count is ORPHANED — it can no
 *    longer be resumed (no `lastRunDate`), so Generate is its only forward path. Gating Generate on
 *    status ALONE dead-ended these rows (no Generate, no Resume) — the reported bug where Hammond/Jena
 *    lost every action after a config edit.
 *
 * Keying on the absence of `lastRunDate` keeps Generate and Start/Resume mutually exclusive: a live
 * Draft/InProgress (lastRunDate present) is Resume-only, an orphaned one (lastRunDate absent) is
 * Generate-only. The server's Generate has no active-session guard, so generating over an orphaned
 * row is safe. This mirrors the scheduled worker's own guard (`CycleCountSchedulerUtils` skips only
 * `ActiveSessionExists`). Generating stamps a new `LastRunDate = now`, re-anchoring the cadence — the
 * same effect a scheduled run has. The transient "generating…" spinner is a separate UI concern the
 * caller layers on top.
 */
export function canGenerateCount(
  config: Pick<CycleCountConfig, 'cycleCountStatus' | 'lastRunDate'>
): boolean {
  const isLiveResumableSession =
    !!config.lastRunDate &&
    (config.cycleCountStatus === 'Draft' ||
      config.cycleCountStatus === 'InProgress');
  return !isLiveResumableSession;
}

/**
 * Label for the resume action. An InProgress count is already underway ("Resume");
 * a Draft count has been generated but not yet started ("Start").
 */
export function getCycleCountResumeLabel(
  status: CycleCountStatus | null
): string {
  return status === 'InProgress' ? 'Resume Cycle Count' : 'Start Cycle Count';
}
